import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BusinessIcon from "@mui/icons-material/Business";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSelector } from "react-redux";
import { selectCustodians, selectAccountingData } from "../../Redux/Reducers/selectors";
import { findCust } from "../../component/Methods";
import { getEntityDateColumn } from "../../utils/entityDateColumn";

const FundsTable = ({
  funds,
  onAdd,
  onAddSMA,
  onEdit,
  onDelete,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortModel, setSortModel] = useState([
    { field: "abbreviation", sort: "asc" },
  ]);
  const open = Boolean(anchorEl);

  const custodians = useSelector(selectCustodians);
  const accountingData = useSelector(selectAccountingData);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddFund = () => {
    onAdd();
    handleMenuClose();
  };

  const handleAddSMA = () => {
    onAddSMA();
    handleMenuClose();
  };

  // Process funds data with accounting information like the original implementation
  const processedFunds = useMemo(() => {
    if (!funds || !accountingData) return [];

    const findAum = (key) => {
      if (key && accountingData && accountingData[0] && accountingData[0].funds) {
        const findName = accountingData[0]?.funds?.find((i) => {
          return i.fund == key;
        });
        return findName?.aum;
      } else {
        return 0;
      }
    };

    return funds?.map((row) => {
      const { fundId, accounts } = row;
      const SelectedAum = findAum(fundId);
      const fundAccounts = accounts
        ? Object.keys(accounts).map((key) => ({
            name: key,
            ...accounts[key],
          }))
        : [];

      const countCustodians = accounts ? Object.keys(accounts).length : 0;
      const countAccounts = accounts
        ? Object.values(accounts).reduce((acc, custodian) => {
            if (Array.isArray(custodian)) {
              return acc + custodian.length;
            }
            return acc + 1;
          }, 0)
        : 0;

      return {
        ...row,
        SelectedAum,
        countCustodians,
        countAccounts,
        fundAccounts,
      };
    }) || [];
  }, [funds, accountingData]);

  const compareSortValues = useCallback((a, b) => {
    if (a == null && b == null) return 0;
    if (a == null || a === "") return -1;
    if (b == null || b === "") return 1;
    if (typeof a === "boolean" && typeof b === "boolean") {
      return Number(a) - Number(b);
    }
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }, []);

  const sortRowsByModel = useCallback(
    (rows, model) => {
      const effectiveModel =
        model?.length > 0 ? model : [{ field: "abbreviation", sort: "asc" }];
      const sorted = [...rows];
      [...effectiveModel].reverse().forEach(({ field, sort }) => {
        if (!field || !sort) return;
        const dir = sort === "desc" ? -1 : 1;
        sorted.sort((a, b) => compareSortValues(a?.[field], b?.[field]) * dir);
      });
      return sorted;
    },
    [compareSortValues]
  );

  const columns = [
    {
      field: "__expand",
      headerName: "",
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      colSpan: (_value, row) => (row.isAccountDetailPanel ? 9 : 1),
      renderCell: (params) => {
        if (params.row.isAccountDetailPanel) {
          const accounts = params.row.fundAccounts || [];
          const panelBg =
            theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.grey[50];
          return (
            <Box
              sx={{
                width: "100%",
                py: 1.5,
                px: 3,
                backgroundColor: panelBg,
                boxSizing: "border-box",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Fund Accounts
              </Typography>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    borderColor: theme.palette.divider,
                    py: 0.75,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[50]
                          : theme.palette.grey[100],
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                      Account Names
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                      Custodian
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                      Number
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <TableCell sx={{ fontSize: "0.8125rem" }}>
                        {account?.name || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8125rem" }}>
                        {findCust(account?.custodian, custodians) || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8125rem" }}>
                        {account?.number || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          );
        }
        if (!params.row.hasAccountData) return null;
        const isExpanded =
          expandedRows.has(params.row.id) ||
          expandedRows.has(String(params.row.id));
        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setExpandedRows((prev) => {
                const next = new Set(prev);
                const key = params.row.id;
                if (next.has(key) || next.has(String(key))) {
                  next.delete(key);
                  next.delete(String(key));
                } else {
                  next.add(key);
                }
                return next;
              });
            }}
            sx={{ p: 0.25 }}
          >
            {isExpanded ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "isSMA",
      headerName: "Entity Type",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.isAccountDetailPanel) return null;
        const isSMA = params.value === true;
        return (
          <Chip
            label={isSMA ? "SMA" : "Fund"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              color: isSMA
                ? theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
                : theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32',
              border: `1px solid ${isSMA 
                ? theme.palette.mode === 'dark' ? '#42a5f5' : '#bbdefb'
                : theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'}`,
            }}
          />
        );
      },
    },
    {
      field: "name",
      headerName: "Full Name",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "SelectedAum",
      headerName: "AUM",
      flex: 1,
      minWidth: 120,
      valueFormatter: (valueOrParams) => {
        const n = Number.isFinite(valueOrParams) ? valueOrParams : Number(valueOrParams);
        return `$ ${(Number.isFinite(n) ? n : 0).toLocaleString("en-US")}`;
      },
    },
    {
      field: "countCustodians",
      headerName: "No Of Custodians", 
      flex: 1,
      minWidth: 140,
    },
    {
      field: "countAccounts",
      headerName: "No Of Accounts",
      flex: 1,
      minWidth: 130,
    },
   
    (() => {
      const dateColumn = getEntityDateColumn({
        field: "date",
        headerName: "Creation Date",
        flex: 1,
        minWidth: 140,
      });
      return {
        ...dateColumn,
        renderCell: (params) =>
          params.row.isAccountDetailPanel ? null : dateColumn.renderCell(params),
      };
    })(),
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isAccountDetailPanel) return null;
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onEdit(params.row)}
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light + "20",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">  
              <IconButton
                size="small"
                onClick={() => {
                  onDelete(params.row);
                }}
                sx={{
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: theme.palette.error.light + "20",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const filteredData = useMemo(() => {
    let data = processedFunds;
    if (searchTerm) {
      data = data.filter((fund) =>
        fund.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fund.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return data;
  }, [processedFunds, searchTerm]);

  const rowsWithId = useMemo(() => {
    return filteredData.map((fund) => ({
      ...fund,
      id: fund.fundId || fund.fund,
      hasAccountData: (fund?.fundAccounts || [])?.length > 0,
    }));
  }, [filteredData]);

  const flattenedRows = useMemo(() => {
    const topLevel = sortRowsByModel(
      rowsWithId.filter((row) => !row?.isAccountDetailPanel),
      sortModel
    );
    const list = [];
    topLevel.forEach((row) => {
      list.push(row);
      const isExpanded =
        expandedRows.has(row.id) || expandedRows.has(String(row.id));
      if (isExpanded && row.hasAccountData) {
        list.push({
          id: `${row.id}__accounts_panel`,
          isAccountDetailPanel: true,
          parentId: row.id,
          fundAccounts: row.fundAccounts || [],
          hasAccountData: false,
        });
      }
    });
    return list.map((row, index) => ({ ...row, __treeOrder: index }));
  }, [rowsWithId, expandedRows, sortModel, sortRowsByModel]);

  const treeOrderById = useMemo(() => {
    const map = new Map();
    flattenedRows.forEach((row) => {
      map.set(String(row.id), row.__treeOrder);
    });
    return map;
  }, [flattenedRows]);

  const getTreeOrderSortComparator = useCallback(
    () => (v1, v2, cellParams1, cellParams2) => {
      const o1 =
        treeOrderById.get(String(cellParams1?.id)) ?? Number.MAX_SAFE_INTEGER;
      const o2 =
        treeOrderById.get(String(cellParams2?.id)) ?? Number.MAX_SAFE_INTEGER;
      return o1 - o2;
    },
    [treeOrderById]
  );

  const gridColumns = useMemo(
    () =>
      columns.map((col) =>
        col.sortable === false
          ? col
          : { ...col, getSortComparator: getTreeOrderSortComparator }
      ),
    [columns, getTreeOrderSortComparator]
  );

  const tableBg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.background.paper;

  return (
    <Box sx={{width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search funds..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleMenuClick}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Entity
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleAddFund} sx={{ zoom: 0.8 }}>
              <ListItemIcon>
                <AccountBalanceWalletIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Fund</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddSMA} sx={{ zoom: 0.8 }}>
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add SMA</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <DataGrid
        rows={flattenedRows}
        columns={gridColumns}
        getRowId={(row) => row.id}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model || [])}
        sortingMode="client"
        paginationModel={{
          page: page || 0,
          pageSize: Math.min(pageSize || 1000, 1000),
        }}
        onPaginationModelChange={(model) => {
          onPageChange?.(model.page);
          onPageSizeChange?.(Math.min(model.pageSize, 1000));
        }}
        pageSizeOptions={[100, 200, 1000]}
        pagination
        density="compact"
        disableRowSelectionOnClick
        getRowHeight={(params) => {
          if (params.model.isAccountDetailPanel) {
            const count = params.model.fundAccounts?.length || 0;
            return 72 + count * 36 + 24;
          }
          return null;
        }}
        getRowClassName={(params) =>
          params.row.isAccountDetailPanel ? "funds-account-panel" : ""
        }
        isRowSelectable={(params) => !params.row.isAccountDetailPanel}
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          backgroundColor: tableBg,
          "& .MuiDataGrid-main": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-row.funds-account-panel": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[100]
                : theme.palette.grey[50],
          },
          "& .MuiDataGrid-row.funds-account-panel .MuiDataGrid-cell": {
            borderBottom: "none",
            paddingTop: 0,
            paddingBottom: 0,
            alignItems: "stretch",
            width: "100% !important",
            maxWidth: "none !important",
            flex: "1 0 100% !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </Box>
  );
};

export default FundsTable;
